/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';
import type { Interpolation } from '@emotion/react';

import * as React from 'react';
import { useContext } from 'react';

import { useRetracked } from 'js/lib/retracked';

import { CardMetadata, CardSkills, ProductCard } from '@coursera/cds-core';
import type { Theme } from '@coursera/cds-core';
import { useTracker, useVisibilityTracker } from '@coursera/event-pulse/react';

import { ProfileContext } from 'bundles/account-profile/components/recruiting-profile-viewer/ProfileContextProvider';
import { EVENTS } from 'bundles/account-profile/components/recruiting-profile-viewer/eventing/events';
import { getOccupationLabelWithId } from 'bundles/account-profile/components/recruiting-profile-viewer/utils/projectUtils';
import type { Occupation } from 'bundles/account-profile/components/recruiting-profile-viewer/utils/projectUtils';
import TrackedDiv from 'bundles/page/components/TrackedDiv';
import type { SharedPortfolioProject_ProjectDetailsFragment as PortfolioProjectDetails } from 'bundles/portfolio-projects/queries/__generated__/SharedPortfolioProject_ProjectDetails';

import CourseraLogo from 'images/favicons/favicon-v2-32x32.png';

import _t from 'i18n!nls/account-profile';

type PropsFromCaller = {
  trackingName?: typeof EVENTS.profileBrowseProjectSelectProject;
  project: PortfolioProjectDetails;
  occupations: Occupation[];
  customCss?: Interpolation<Theme>;
  index?: number;
  activeFilters: Record<string, boolean>;
};

const styles = {
  projectCard: (theme: Theme) => css`
    border: 2px solid ${theme.palette.gray[300]};

    div > a {
      display: inline-block;
      width: 100%;
    }
  `,
};

const BrowseProjectProductCard = ({
  project,
  trackingName = EVENTS.profileBrowseProjectSelectProject,
  occupations,
  customCss,
  index = 0,
  activeFilters,
}: PropsFromCaller) => {
  const { profileState, sharedEventingV3Data } = useContext(ProfileContext);
  const retrackedTrack = useRetracked();
  const track = useTracker();

  const selectedFilters = Object.entries(activeFilters)
    .map(([filterName, filterValue]) => {
      if (filterValue) {
        return filterName;
      } else {
        return '';
      }
    })
    .filter((filterName) => !!filterName);

  const visibilityTrackingRef: React.MutableRefObject<HTMLAnchorElement | null> = useVisibilityTracker(
    'view_product_card',
    {
      productCard: {
        index,
      },
      product: {
        id: project.id,
        slug: project.slug,
        name: trackingName,
      },
      filter: selectedFilters,
      enterprise: {
        ...sharedEventingV3Data.enterprise,
      },
    }
  );

  const trackClick = () => {
    retrackedTrack({
      trackingName,
      trackingData: {
        project_id: project.id,
        projectSlug: project.slug,
        projects_filters: activeFilters,
      },
      action: 'click',
    });

    track('click_product_card', {
      productCard: {
        index,
      },
      product: {
        id: project.id,
        slug: project.slug,
        name: trackingName,
      },
      filter: selectedFilters,
      enterprise: {
        ...sharedEventingV3Data.enterprise,
      },
    });
  };

  const occupationLabel = getOccupationLabelWithId(project.occupationId, occupations);
  const estimatedWorkloadAndPacing = `${project.estimatedWorkload} (Self-paced)`;
  return (
    <TrackedDiv
      trackClicks={false}
      withVisibilityTracking
      requireFullyVisible={false}
      trackingName={trackingName}
      data={{
        project_id: project.id,
        projectSlug: project.slug,
      }}
    >
      <ProductCard
        data-testid={`projectCard-${project.occupationId}`}
        css={[styles.projectCard, customCss]}
        variant="list"
        productType="project"
        partners={[
          {
            name: project.partner?.name ?? _t('Coursera Project Network'),
            logoUrl: project.partner?.squareLogo ?? CourseraLogo,
          },
        ]}
        previewImageSrc={project.image}
        title={{
          name: project.title,
          linkProps: {
            href: `/projects/${project.slug}`,
            target: '_blank',
            rel: 'noopener noreferrer',
            ref: visibilityTrackingRef,
            onClick: trackClick,
          },
        }}
        body={
          project.skills.length > 0 && (
            <>
              <CardSkills prefix={_t("Skills you'll demonstrate")} skills={project.skills} />
            </>
          )
        }
        footer={
          <>
            <CardMetadata metadata={[occupationLabel, estimatedWorkloadAndPacing].filter((metadata) => !!metadata)} />
          </>
        }
      />
    </TrackedDiv>
  );
};

export default BrowseProjectProductCard;
